import {
  createContext,
  type FC,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';

import EppoFlags from '@/EppoFlags';
import {useStringAssignmentExperimentDetails} from '@/eppoHooks';
import {type JumpBackInFulfillmentDetailFragment, useLoggedOutHomePageQuery} from '@/graphql/types';
import {buildJumpBackInCards, type JumpBackInCardData} from './jumpBackInCards';

export enum JumpBackInHomepageExperimentVariation {
  FALLBACK = 'fallback',
  CONTROL = 'control',
  TREATMENT = 'treatment',
}

const EXPERIMENT_VARIATIONS: string[] = Object.values(JumpBackInHomepageExperimentVariation);

const isExperimentVariation = (value: string): value is JumpBackInHomepageExperimentVariation =>
  EXPERIMENT_VARIATIONS.includes(value);

type JumpBackInHomepageContextType = {
  variation: JumpBackInHomepageExperimentVariation | null;
  trackExperimentExposed: () => void;
  loading: boolean;
  hasFulfillmentDetails: boolean;
  fulfillmentDetails: JumpBackInFulfillmentDetailFragment[];
  cards: JumpBackInCardData[];
};

export const JumpBackInHomepageContext = createContext<JumpBackInHomepageContextType>({
  variation: JumpBackInHomepageExperimentVariation.FALLBACK,
  trackExperimentExposed: () => {},
  loading: true,
  hasFulfillmentDetails: false,
  fulfillmentDetails: [],
  cards: [],
});

export const useJumpBackInHomepage = () => useContext(JumpBackInHomepageContext);

const JumpBackInHomepageProvider: FC<PropsWithChildren> = ({children}) => {
  const hasTrackedExposureRef = useRef(false);

  const {variation: rawVariation, trackExperimentExposed} = useStringAssignmentExperimentDetails(
    EppoFlags.JumpBackInHomepageV2,
  );

  const variation = isExperimentVariation(rawVariation) ? rawVariation : null;

  const {data, loading} = useLoggedOutHomePageQuery();

  const fulfillmentDetails = useMemo(
    () =>
      (data?.identity?.fulfillmentDetails?.edges ?? [])
        .map(edge => edge.node)
        .filter((node): node is JumpBackInFulfillmentDetailFragment => Boolean(node))
        .map(node => {
          const seenCartIds = new Set<string>();

          return {
            ...node,
            consumerCarts: node.consumerCarts.filter(cart => {
              if (seenCartIds.has(cart.id)) return false;
              seenCartIds.add(cart.id);

              return true;
            }),
          };
        }),
    [data],
  );

  const cards = useMemo(() => buildJumpBackInCards(fulfillmentDetails), [fulfillmentDetails]);

  const hasFulfillmentDetails = cards.length > 0;

  const trackExposure = useCallback(() => {
    if (
      variation !== null &&
      variation !== JumpBackInHomepageExperimentVariation.FALLBACK &&
      !hasTrackedExposureRef.current
    ) {
      trackExperimentExposed();
      hasTrackedExposureRef.current = true;
    }
  }, [variation, trackExperimentExposed]);

  const contextValue = useMemo(
    () => ({
      variation,
      trackExperimentExposed: trackExposure,
      loading,
      hasFulfillmentDetails,
      fulfillmentDetails,
      cards,
    }),
    [variation, trackExposure, loading, hasFulfillmentDetails, fulfillmentDetails, cards],
  );

  return (
    <JumpBackInHomepageContext.Provider value={contextValue}>
      {children}
    </JumpBackInHomepageContext.Provider>
  );
};

export default JumpBackInHomepageProvider;
